import type { ChatbotMemberRole } from '../../types/chatbot';

export type ChatbotPermission =
  | 'viewChatbot'
  | 'viewMembers'
  | 'manageMembers'
  | 'leaveChatbot'
  | 'manageTraining'
  | 'editConfiguration'
  | 'manageSettings'
  | 'deleteChatbot';

const CHATBOT_ROLE_PERMISSIONS: Record<
  ChatbotMemberRole,
  ReadonlySet<ChatbotPermission>
> = {
  ADMIN: new Set([
    'viewChatbot',
    'viewMembers',
    'manageMembers',
    'leaveChatbot',
    'manageTraining',
    'editConfiguration',
    'manageSettings',
    'deleteChatbot',
  ]),
  EDITOR: new Set([
    'viewChatbot',
    'viewMembers',
    'leaveChatbot',
    'manageTraining',
    'editConfiguration',
  ]),
  VIEWER: new Set(['viewChatbot', 'viewMembers', 'leaveChatbot']),
};

function hasChatbotPermission(
  role: ChatbotMemberRole | null | undefined,
  permission: ChatbotPermission,
): boolean {
  if (!role) {
    return false;
  }
  return CHATBOT_ROLE_PERMISSIONS[role].has(permission);
}

export function canEditChatbotConfiguration(
  role: ChatbotMemberRole | null | undefined,
): boolean {
  return hasChatbotPermission(role, 'editConfiguration');
}

export function canManageChatbotSettings(
  role: ChatbotMemberRole | null | undefined,
): boolean {
  return hasChatbotPermission(role, 'manageSettings');
}

export function canManageChatbotMembers(
  role: ChatbotMemberRole | null | undefined,
): boolean {
  return hasChatbotPermission(role, 'manageMembers');
}

export function canManageChatbotTraining(
  role: ChatbotMemberRole | null | undefined,
): boolean {
  return hasChatbotPermission(role, 'manageTraining');
}

export function canLeaveChatbot(
  role: ChatbotMemberRole | null | undefined,
): boolean {
  return hasChatbotPermission(role, 'leaveChatbot');
}

/** Admins may change role / remove any other member, including other admins. */
export function canManageChatbotMemberTarget(
  actorRole: ChatbotMemberRole | null | undefined,
  targetUserId: string | undefined,
  currentUserId: string | undefined,
): boolean {
  if (!canManageChatbotMembers(actorRole) || !currentUserId || !targetUserId) {
    return false;
  }
  return targetUserId !== currentUserId;
}
